import { format as dateFnsFormat, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tippy from '~components/tippy';
import CaptionSelect from './caption-select';
import parseValueToDate from './helpers/parse-value-to-date';
import Input from './input';
import StyledDayPicker from './styled-day-picker';

const DayPickerInput = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    defaultValue,
    value: valueProp,
    defaultMonth,
    month: monthProp,
    format,
    disabled,
    placeholder,
    onChange,
    onMonthChange,
    InputComponent: InputComponentProp,
    TippyProps,
    InputProps,
    DayPickerProps,
    CaptionSelectProps,
    ...otherProps
  } = props;
  const [tippyInstance, setTippyInstance] = useState();
  const isValueControlled = useRef(valueProp !== undefined && valueProp !== null);
  const isMonthControlled = useRef(monthProp !== undefined && monthProp !== null);

  const defaultDate = useMemo(() => parseValueToDate(defaultValue, format), [defaultValue, format]);
  const [internalSelectedDays, setInternalSelectedDays] = useState(
    isValid(defaultDate) ? defaultDate : null
  );
  const [internalValue, setInternalValue] = useState(defaultValue);

  const inputValue = valueProp || internalValue;

  const selectedDays = useMemo(
    () => (isValueControlled.current ? parseValueToDate(valueProp, format) : internalSelectedDays),
    [format, internalSelectedDays, valueProp]
  );

  const [internalMonth, internalSetMonth] = useState(
    defaultMonth ?? (isValid(selectedDays) ? selectedDays : new Date())
  );

  const month = isMonthControlled.current ? monthProp : internalMonth;

  const InputComponent = InputComponentProp ?? Input;

  const handleMonthChange = (date) => {
    if (onMonthChange) onMonthChange(date);
    if (!isMonthControlled.current) internalSetMonth(date);
  };

  const handleCaptionSelectChange = (date) => {
    handleMonthChange(date);
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    const date = parseValueToDate(value, format);
    if (!isValueControlled.current) {
      setInternalSelectedDays(date);
      setInternalValue(value);
    }
    if (onChange) onChange(date, value);
    if (isValid(date)) handleMonthChange(date);
  };

  const handleDayClick = (date, modifiers) => {
    if (modifiers.disabled) return;
    const value = dateFnsFormat(date, format);
    if (!isValueControlled.current) {
      setInternalSelectedDays(date);
      setInternalValue(value);
    }
    if (onChange) onChange(date, value);
    tippyInstance.hide();
  };

  const handleTippyCreate = (instance) => {
    setTippyInstance(instance);
    if (TippyProps?.onCreate) TippyProps.onCreate(instance);
  };

  return (
    <Tippy
      ref={ref}
      theme="popper"
      disabled={disabled}
      placement="bottom-start"
      appendTo={window.document.body}
      content={
        <StyledDayPicker
          selectedDays={[selectedDays]}
          captionElement={(captionProps) => (
            <CaptionSelect
              {...captionProps}
              onChange={handleCaptionSelectChange}
              {...CaptionSelectProps}
            />
          )}
          onDayClick={handleDayClick}
          {...DayPickerProps}
          month={month}
          onMonthChange={handleMonthChange}
        />
      }
      {...TippyProps}
      onCreate={handleTippyCreate}
    >
      <InputComponent
        tippyInstance={tippyInstance}
        autoComplete="off"
        disabled={disabled}
        value={inputValue}
        placeholder={placeholder ?? t('select_a_date')}
        onChange={handleInputChange}
        readOnly
        {...InputProps}
        {...otherProps}
      />
    </Tippy>
  );
});

DayPickerInput.propTypes = {
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  defaultMonth: PropTypes.instanceOf(Date),
  month: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  format: PropTypes.string,
  onChange: PropTypes.func,
  onMonthChange: PropTypes.func,
  TippyProps: PropTypes.object,
  InputProps: PropTypes.object,
  DayPickerProps: PropTypes.object,
  CaptionSelectProps: PropTypes.object,
  InputComponent: PropTypes.any,
};

DayPickerInput.defaultProps = {
  defaultValue: undefined,
  value: undefined,
  defaultMonth: undefined,
  month: undefined,
  disabled: undefined,
  placeholder: undefined,
  format: 'MM/dd/yyyy',
  onChange: undefined,
  onMonthChange: undefined,
  TippyProps: undefined,
  InputProps: {},
  DayPickerProps: {},
  CaptionSelectProps: undefined,
  InputComponent: undefined,
};

export default DayPickerInput;
